// Offline Storage Service using IndexedDB for tickets and event details
import { openDB } from 'idb';

const DB_NAME = 'PaddleTurkiyeDB';
const DB_VERSION = 1;

class OfflineStorage {
  constructor() {
    this.db = null;
    this.initDB();
  }

  // Initialize IndexedDB
  async initDB() {
    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Create object stores
          if (!db?.objectStoreNames?.contains('tickets')) {
            db?.createObjectStore('tickets', { keyPath: 'id' });
          }
          if (!db?.objectStoreNames?.contains('events')) {
            db?.createObjectStore('events', { keyPath: 'id' });
          }
          if (!db?.objectStoreNames?.contains('bookings')) {
            db?.createObjectStore('bookings', { keyPath: 'id' });
          }
          if (!db?.objectStoreNames?.contains('weatherData')) {
            db?.createObjectStore('weatherData', { keyPath: 'eventId' });
          }
        }
      });
      console.log('IndexedDB initialized successfully');
    } catch (error) {
      console.error('Error initializing IndexedDB:', error);
    }
  }

  // Save tickets to offline storage
  async saveTickets(tickets) {
    if (!this.db) await this.initDB();
    
    try {
      const tx = this.db?.transaction('tickets', 'readwrite');
      const store = tx?.objectStore('tickets');
      
      for (const ticket of tickets) {
        await store?.put(ticket);
      }
      
      await tx?.done;
      console.log('Tickets saved to offline storage');
      return true;
    } catch (error) {
      console.error('Error saving tickets:', error);
      return false;
    }
  }

  // Get tickets from offline storage
  async getTickets() {
    if (!this.db) await this.initDB();
    
    try {
      const tickets = await this.db?.getAll('tickets');
      return tickets;
    } catch (error) {
      console.error('Error getting tickets:', error);
      return [];
    }
  }

  // Save event details to offline storage
  async saveEvent(event) {
    if (!this.db) await this.initDB();
    
    try {
      const tx = this.db?.transaction('events', 'readwrite');
      await tx?.objectStore('events')?.put(event);
      await tx?.done;
      console.log('Event saved to offline storage');
      return true;
    } catch (error) {
      console.error('Error saving event:', error);
      return false;
    }
  }

  // Get event from offline storage
  async getEvent(eventId) {
    if (!this.db) await this.initDB();
    
    try {
      const event = await this.db?.get('events', eventId);
      return event;
    } catch (error) {
      console.error('Error getting event:', error);
      return null;
    }
  }

  // Save booking to offline storage
  async saveBooking(booking) {
    if (!this.db) await this.initDB();
    
    try {
      const tx = this.db?.transaction('bookings', 'readwrite');
      await tx?.objectStore('bookings')?.put(booking);
      await tx?.done;
      console.log('Booking saved to offline storage');
      return true;
    } catch (error) {
      console.error('Error saving booking:', error);
      return false;
    }
  }

  // Get all bookings from offline storage
  async getBookings() {
    if (!this.db) await this.initDB();
    
    try {
      const bookings = await this.db?.getAll('bookings');
      return bookings;
    } catch (error) {
      console.error('Error getting bookings:', error);
      return [];
    }
  }

  // Save weather data to offline storage
  async saveWeatherData(eventId, weatherData) {
    if (!this.db) await this.initDB();
    
    try {
      const data = {
        eventId,
        ...weatherData,
        lastUpdated: new Date()?.toISOString()
      };
      
      const tx = this.db?.transaction('weatherData', 'readwrite');
      await tx?.objectStore('weatherData')?.put(data);
      await tx?.done;
      console.log('Weather data saved to offline storage');
      return true;
    } catch (error) {
      console.error('Error saving weather data:', error);
      return false;
    }
  }

  // Get weather data from offline storage
  async getWeatherData(eventId) {
    if (!this.db) await this.initDB();
    
    try {
      const weatherData = await this.db?.get('weatherData', eventId);
      return weatherData;
    } catch (error) {
      console.error('Error getting weather data:', error);
      return null;
    }
  }

  // Sync localStorage data to IndexedDB
  async syncFromLocalStorage() {
    try {
      // Sync tickets
      const tickets = JSON.parse(localStorage.getItem('userTickets') || '[]');
      if (tickets?.length > 0) {
        await this.saveTickets(tickets);
      }

      // Sync bookings
      const bookings = JSON.parse(localStorage.getItem('bookings') || '[]');
      for (const booking of bookings) {
        await this.saveBooking(booking);
      }

      console.log('Data synced from localStorage to IndexedDB');
      return true;
    } catch (error) {
      console.error('Error syncing from localStorage:', error);
      return false;
    }
  }

  // Clear all offline data
  async clearAll() {
    if (!this.db) await this.initDB();
    
    try {
      const stores = ['tickets', 'events', 'bookings', 'weatherData'];
      for (const storeName of stores) {
        const tx = this.db?.transaction(storeName, 'readwrite');
        await tx?.objectStore(storeName)?.clear();
        await tx?.done;
      }
      console.log('All offline data cleared');
      return true;
    } catch (error) {
      console.error('Error clearing offline data:', error);
      return false;
    }
  }
}

// Export singleton instance
const offlineStorage = new OfflineStorage();
export default offlineStorage;
